const createCart = `
  mutation CreateLookupCart($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        lines(first: 1) {
          nodes {
            quantity
            merchandise {
              ... on ProductVariant {
                id
              }
            }
          }
        }
      }
      userErrors {
        message
      }
    }
  }
`;

const applyGiftCard = `
  mutation CheckGiftCard($cartId: ID!, $codes: [String!]!) {
    cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $codes) {
      cart {
        appliedGiftCards {
          balance {
            amount
            currencyCode
          }
          amountUsed {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        message
      }
    }
  }
`;

class GiftCardCheck extends HTMLElement {
    connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.form = this.querySelector('form');
        this.input = this.querySelector('[name="gift_card_code"]');
        this.submit = this.querySelector('[type="submit"]');
        this.result = this.querySelector('[role="status"]');
        this.resetButtons = this.querySelectorAll('[data-gift-card-reset]');
        this.backButton = this.querySelector('.gift-card-check__back');
        this.details = this.querySelector('[data-gift-card-details]');
        this.balance = this.querySelector('[data-gift-card-balance]');
        this.resultImage = this.querySelector('[data-gift-card-result-image]');
        this.backgroundBefore = this.querySelector('[data-gift-card-background-before]');
        this.backgroundAfter = this.querySelector('[data-gift-card-background-after]');

        this.input.addEventListener('input', () => {
            const value = this.input.value.replace(/\s/g, '');
            this.input.value = value.match(/.{1,4}/g)?.join(' ') || '';
        });

        this.form.addEventListener('submit', event => this.lookup(event));

        this.resetButtons.forEach(button => {
            button.addEventListener('click', () => this.resetLookup());
        });
    }

    setResultState(showResult) {
        this.form.hidden = showResult;
        this.details.hidden = !showResult;
        this.backButton.hidden = !showResult;
        this.classList.toggle('gift-card-check--balance', showResult);
        if (this.resultImage) {
            this.resultImage.hidden = !showResult;
        }
        if (this.backgroundAfter) {
            this.backgroundAfter.hidden = !showResult;
            if (this.backgroundBefore) {
                this.backgroundBefore.hidden = showResult;
            }
        }
    }

    resetLookup() {
        this.form.reset();
        this.setResultState(false);
        this.balance.textContent = '';
        this.result.textContent = '';
        this.input.focus();
    }

    async request(query, variables, operation) {
        const response = await fetch(this.dataset.endpoint, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query,
                variables
            })
        });

        if (!response.ok) {
            throw new Error('Lookup request failed');
        }

        const result = await response.json();
        const payload = result.data && result.data[operation];

        if (
            result.errors?.length ||
            !payload?.cart ||
            payload.userErrors?.length
        ) {
            throw new Error('Shopify rejected the lookup request');
        }

        return payload.cart;
    }

    async lookup(event) {
        event.preventDefault();

        if (this.submit.disabled) return;

        const code = this.input.value.replace(/\s/g, '');

        if (!code) {
            this.result.textContent = 'Enter your gift card code.';
            this.input.focus();
            return;
        }

        this.submit.disabled = true;
        this.result.textContent = '';

        try {
            const temporaryCart = await this.request(
                createCart,
                {
                    input: this.dataset.variantId ? {
                    lines: [
                        {
                            merchandiseId: this.dataset.variantId,
                            quantity: 1
                        }
                    ]
                    } : {}
                },
            'cartCreate'
            );

            const line = temporaryCart.lines?.nodes?.[0];

            if (
                !temporaryCart.id ||
                (this.dataset.variantId && (
                    line?.merchandise?.id !== this.dataset.variantId ||
                    line?.quantity !== 1
                ))
            ) {
                throw new Error('Test product unavailable');
            }

            const cart = await this.request(
                applyGiftCard,
                {
                    cartId: temporaryCart.id,
                    codes: [code]
                },
                'cartGiftCardCodesUpdate'
            );

            const giftCard = cart.appliedGiftCards?.[0];

            if (!giftCard?.balance || !giftCard?.amountUsed) {
                throw new Error('Gift card not found');
            }

            this.balance.textContent = formatMoney(
                Math.round(Number(giftCard.balance.amount) * 100),
                this.dataset.moneyFormat
            );
            this.result.textContent = '';

            this.input.value = '';
            this.setResultState(true);
            this.querySelector('.gift-card-check__balance-label').focus();
        } catch (error) {
        if (error.message === 'Gift card not found') {
            this.result.textContent = "That code wasn't found, check it and try again.";
        } else {
            this.result.textContent = 'Unable to check your balance right now. Please try again.';
        }
        } finally {
            this.submit.disabled = false;
        }
    }
}

if (!customElements.get('gift-card-check')) {
    customElements.define('gift-card-check', GiftCardCheck);
}